import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { LaunchDetails, LaunchSegment } from '../../datasource/generated';
import creditSubmissionSlice from '../../datasource/mutations/creditSubmission';
import launchSlice from '../../datasource/mutations/launch';
import { CachedImage } from '../../utility/imageUtils';
import {
    BrowserMediaDiagnostics,
    createInitialPlayerCommandDiagnostics,
    createInitialPlayerDiagnosticsState,
    createInitialRangeProbeDiagnostics,
    createInitialSeekDiagnostics,
    PlayerCommandDiagnostics,
    PlayerDiagnosticsState,
    PlayerVideoSourceType,
    RangeProbeDiagnostics,
    SeekDiagnostics,
} from '../../utility/playerDiagnostics';

export enum ExternalCommand {
    play,
    pause,
    noop,
}

export enum ExternalFullscreenToggle {
    on,
    off,
    noop,
}

export interface PlaybackPosition {
    itemSk: number,
    position: number,
    totalTime: number,
}

export type PossibleThreshold = 10 | 25 | 50 | 75

export interface PlayerState {
    itemSk: number,
    itemPk: string,
    programSk: number,
    currentSegmentItemSk: number;
    playbackProgress: PlaybackPosition[];
    progressTracked: { 
        [itemSk: number]: PossibleThreshold[] 
    };
    launchDetails: LaunchDetails;
    isFetchingLaunchDetails: boolean;
    hasLoadedLaunchDetails: boolean;
    streamCleared: boolean;
    currentPrgSegmentSk: number;
    didPosterImageFinishCaching: boolean;
    didSegmentThumbnailsFinishedCaching: boolean;
    cachedImages: {
        [requestedUrl: string]: string
    };
    sessionGuid: string;
    isPlayerElementReady: boolean;
    externalCommand: ExternalCommand;
    externalFullscreenToggle: ExternalFullscreenToggle;
    externalSeekCommand?: number;
    requestStartTime?: number;
    lastVideoStartedFrom: 'auto_played' | 'end_of_video_promotion' | 'segments_panel' | 'notes';
    videoSourceType: PlayerVideoSourceType;
    isPlayerDiagnosticsActive: boolean;
    diagnostics: PlayerDiagnosticsState;
}

const initialState: PlayerState = {
    itemSk: 0,
    itemPk: '0',
    programSk: 0,
    currentSegmentItemSk: 0,
    playbackProgress: [],
    progressTracked: {},
    launchDetails: {},
    isFetchingLaunchDetails: false,
    hasLoadedLaunchDetails: false,
    streamCleared: false,
    currentPrgSegmentSk: 0,
    didPosterImageFinishCaching: false,
    didSegmentThumbnailsFinishedCaching: false,
    cachedImages: {},
    sessionGuid: '',
    isPlayerElementReady: false,
    externalCommand: ExternalCommand.noop,
    externalFullscreenToggle: ExternalFullscreenToggle.noop,
    externalSeekCommand: undefined,
    requestStartTime: undefined,
    lastVideoStartedFrom: 'auto_played',
    videoSourceType: null,
    isPlayerDiagnosticsActive: false,
    diagnostics: createInitialPlayerDiagnosticsState(),
};

const playerSlice = createSlice({
    name: 'player',
    initialState,
    reducers: {
        setItemSk(state, action: PayloadAction<number>) {
            state.itemSk = action.payload;
        },
        setItemPk(state, action: PayloadAction<string>) {
            state.itemPk = action.payload;
        },
        setCurrentSegmentItemSk(state, action: PayloadAction<number>) {
            state.currentSegmentItemSk = action.payload;
        },
        setCurrentPrgSegmentSk(state, action: PayloadAction<number>) {
            state.currentPrgSegmentSk = action.payload;
        },
        pushPosterImageCachedImages(state, action: PayloadAction<CachedImage[]>) {
            action.payload.map((obj) => {
                state.cachedImages[obj.requestedUrl] = obj.resultingUrl;
            });
            state.didPosterImageFinishCaching = true;
        },
        pushSegmentThumbnailsCachedImages(state, action: PayloadAction<CachedImage[]>) {
            action.payload.map((obj) => {
                state.cachedImages[obj.requestedUrl] = obj.resultingUrl;
            });
            state.didSegmentThumbnailsFinishedCaching = true;
        },
        setSessionGuid(state, action: PayloadAction<string>) {
            state.sessionGuid = action.payload;
        },
        setPlayerElementReady(state) {
            state.isPlayerElementReady = true;
        },
        setExternalCommand(state, action: PayloadAction<ExternalCommand>) {
            state.externalCommand = action.payload;
        },
        setExternalFullscreenToggle(state, action: PayloadAction<ExternalFullscreenToggle>) {
            state.externalFullscreenToggle = action.payload;
        },
        setExternalSeekCommand(state, action: PayloadAction<number | undefined>) {
            state.externalSeekCommand = action.payload;
        },
        setRequestStartTime(state, action: PayloadAction<number | undefined>) {
            state.requestStartTime = action.payload;
        },
        updatePlaybackProgress(state, action: PayloadAction<Omit<PlaybackPosition, 'totalTime'>>) {
            const index = state.playbackProgress.findIndex(x => x.itemSk === action.payload.itemSk);
            if (index > -1)
                state.playbackProgress[index].position = action.payload.position;
        },
        setIsOnAir(state) {
            state.launchDetails.isOffAir = false;
        },
        setLaunchDetails(state, action: PayloadAction<LaunchDetails>) {
            // For storybook
            state.launchDetails = action.payload;
        },
        setVideoUrl(state, action: PayloadAction<string | null | undefined>) {
            const video = state.launchDetails.video;
            const url = action.payload?.trim();
            if (!video || !url) {
                return;
            }

            state.streamCleared = false;
            video.url = url;
            if (video.streaming?.hls) {
                video.streaming.hls.url = url;
            }
        },
        clearActiveStream(state) {
            state.streamCleared = true;
            state.videoSourceType = null;
        },
        setProgressTracked(state, { payload }: PayloadAction<{ itemSk: number, threshold: PossibleThreshold }>) {
            if (!state.progressTracked[payload.itemSk]) {
                state.progressTracked[payload.itemSk] = [];
            }

            state.progressTracked[payload.itemSk].push(payload.threshold);
        },
        setLastVideoStartedFrom(state, { payload }: PayloadAction<typeof state.lastVideoStartedFrom>) {
            state.lastVideoStartedFrom = payload;
        },
        setVideoSourceType(state, action: PayloadAction<null | 'hls' | 'standard'>) {
            state.videoSourceType = action.payload;
        },
        setPlayerDiagnosticsActive(state, action: PayloadAction<boolean>) {
            state.isPlayerDiagnosticsActive = action.payload;
        },
        setPlayerDiagnosticsPlayback(state, action: PayloadAction<Partial<Pick<PlayerDiagnosticsState, 'currentFile' | 'currentPosition' | 'currentDuration' | 'playbackState'>>>) {
            state.diagnostics = {
                ...state.diagnostics,
                ...action.payload,
            };
        },
        setPlayerDiagnosticsBrowserMedia(state, action: PayloadAction<Partial<BrowserMediaDiagnostics>>) {
            state.diagnostics.browserMedia = {
                ...state.diagnostics.browserMedia,
                ...action.payload,
            };
        },
        setPlayerDiagnosticsRangeProbe(state, action: PayloadAction<Partial<RangeProbeDiagnostics>>) {
            state.diagnostics.rangeProbe = {
                ...state.diagnostics.rangeProbe,
                ...action.payload,
            };
        },
        setPlayerDiagnosticsSeek(state, action: PayloadAction<Partial<SeekDiagnostics>>) {
            state.diagnostics.lastSeek = {
                ...state.diagnostics.lastSeek,
                ...action.payload,
            };
        },
        setPlayerDiagnosticsCommand(state, action: PayloadAction<Partial<PlayerCommandDiagnostics>>) {
            state.diagnostics.lastCommand = {
                ...state.diagnostics.lastCommand,
                ...action.payload,
            };
        },
        setPlayerDiagnosticsPlayPauseCommand(state, action: PayloadAction<Partial<PlayerCommandDiagnostics>>) {
            state.diagnostics.lastPlayPauseCommand = {
                ...state.diagnostics.lastPlayPauseCommand,
                ...action.payload,
            };
        },
        resetPlayerDiagnosticsTestResults(state) {
            const {
                browserMedia,
                currentDuration,
                currentFile,
                currentPosition,
                playbackState,
            } = state.diagnostics;

            state.diagnostics = {
                ...createInitialPlayerDiagnosticsState(),
                currentFile,
                currentPosition,
                currentDuration,
                playbackState,
                browserMedia,
                rangeProbe: createInitialRangeProbeDiagnostics(state.videoSourceType, currentFile),
                lastSeek: createInitialSeekDiagnostics(),
                lastPlayPauseCommand: createInitialPlayerCommandDiagnostics(),
                lastCommand: createInitialPlayerCommandDiagnostics(),
            };
        },
        resetPlayerDiagnosticsForMedia(state, action: PayloadAction<{ currentFile: string; sourceType: PlayerVideoSourceType }>) {
            state.diagnostics = {
                ...createInitialPlayerDiagnosticsState(),
                currentFile: action.payload.currentFile,
                rangeProbe: {
                    ...state.diagnostics.rangeProbe,
                    ...createInitialPlayerDiagnosticsState().rangeProbe,
                    sourceType: action.payload.sourceType,
                    url: action.payload.currentFile,
                    status: 'unknown',
                },
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                launchSlice.endpoints.launch.matchPending,
                (state) => {
                    state.isFetchingLaunchDetails = true;
                }
            )
            .addMatcher(
                launchSlice.endpoints.launch.matchFulfilled,
                (state, { payload }) => {
                    state.hasLoadedLaunchDetails = true;
                    state.itemSk = payload.itemSk!;
                    state.itemPk = payload.itemPk!;
                    state.programSk = payload.programSk!;
                    state.currentSegmentItemSk = payload.currentSegment!;
                    state.currentPrgSegmentSk = payload.segments?.find((s: LaunchSegment) => s.itemSk == payload.currentSegment)?.prgSegmentSk ?? 0;
                    state.launchDetails = payload;
                    state.streamCleared = false;
                    state.isFetchingLaunchDetails = false;
                    state.diagnostics = createInitialPlayerDiagnosticsState();
                    if (payload.isWebcast) {
                        state.playbackProgress = [];
                    } else if (payload.video) {
                        const totalTime = getTotalDurationInSeconds(payload.producedRunningTime ?? '');
                        state.playbackProgress = [
                            { 
                                itemSk: payload.currentSegment!, 
                                position: payload.video.position ?? 0, 
                                totalTime 
                            }
                        ];
                    } else {
                        state.playbackProgress = payload.segments?.map(segment => {
                            const totalTime = getTotalDurationInSeconds(segment.producedRunningTime ?? '');
                            const segmentPlaybackPosition: PlaybackPosition = {
                                itemSk: segment.itemSk!,
                                position: segment.video?.position ?? 0,
                                totalTime
                            };
                            return segmentPlaybackPosition;
                        }) ?? [];
                    }
                }
            )
            .addMatcher(
                launchSlice.endpoints.launch.matchRejected,
                (state) => {
                    state.hasLoadedLaunchDetails = true;
                    state.isFetchingLaunchDetails = false;
                }
            )
            .addMatcher(
                creditSubmissionSlice.endpoints.submitCreditRequest.matchFulfilled,
                (state, { payload }) => {
                    state.launchDetails.encryptedRegistrationId = payload.encryptedRegistrationId;
                }
            );
    },
});

const getTotalDurationInSeconds = (producedRunningTime: string) => {
    const time = producedRunningTime?.split(':');
    const hoursInSeconds = parseInt(time ? time[0] : '0') * 60 * 60;
    const minutesInSeconds = parseInt(time ? time[1] : '0') * 60;
    const seconds = parseInt(time ? time[2] : '0');

    return hoursInSeconds + minutesInSeconds + seconds;
};

export const {
    setItemSk,
    setItemPk,
    setCurrentSegmentItemSk,
    setCurrentPrgSegmentSk,
    pushPosterImageCachedImages,
    pushSegmentThumbnailsCachedImages,
    setSessionGuid,
    setPlayerElementReady,
    setExternalCommand,
    setExternalFullscreenToggle,
    setExternalSeekCommand,
    setRequestStartTime,
    updatePlaybackProgress,
    setIsOnAir,
    setLaunchDetails,
    setVideoUrl,
    clearActiveStream,
    setProgressTracked,
    setLastVideoStartedFrom,
    setVideoSourceType,
    setPlayerDiagnosticsActive,
    setPlayerDiagnosticsPlayback,
    setPlayerDiagnosticsBrowserMedia,
    setPlayerDiagnosticsRangeProbe,
    setPlayerDiagnosticsSeek,
    setPlayerDiagnosticsCommand,
    setPlayerDiagnosticsPlayPauseCommand,
    resetPlayerDiagnosticsTestResults,
    resetPlayerDiagnosticsForMedia,
} = playerSlice.actions;

export const { reducer: playerReducer } = playerSlice;
